import { type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import { collectLinkableFrames } from '@/lib/text-linking/linkable-text-frame'
import type { CenterCanvasConfig } from '../types/center-canvas-config'
import type { TextLinkingSession } from '../types/text-linking-session'

/**
 * Text linking mode (WMPCM-6852): the linking session, the Text Tool hover "+", the port/unlink
 * handlers the linking overlay raises, the screen-reader announcements for linking-mode
 * transitions, and the linkable-frame hit test. Cancels any session on spread switch.
 */
export function useTextLinking({
  internalShapeManagerRef,
  unifiedLayerIdsRef,
  checkpointLoadKey,
  onTextFramesLinked,
  onTextFramesUnlinked,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  unifiedLayerIdsRef: MutableRefObject<string[]>
  checkpointLoadKey: CenterCanvasConfig['checkpointLoadKey']
  onTextFramesLinked: CenterCanvasConfig['onTextFramesLinked']
  onTextFramesUnlinked: CenterCanvasConfig['onTextFramesUnlinked']
}) {
  // Text linking mode (WMPCM-6852) — null when idle; set when a frame's out-port is clicked.
  const [textLinking, setTextLinking] = useState<TextLinkingSession | null>(null)
  // Bumped after each text commit/selection change so the linking overlay re-reads live frame geometry.
  const [textLinkingRevision, setTextLinkingRevision] = useState(0)
  const isTextLinkingActive = textLinking != null

  // Text Tool hover "+" entry point (WMPCM-6852 AC12) — which linkable frame (if any) the
  // pointer is currently over while the Text Tool is active and not yet linking.
  const [textToolHoverFrameId, setTextToolHoverFrameId] = useState<string | null>(null)
  // Tracks the pointer-down → pointer-up drag for "drag on canvas to create a new linked frame at a
  // user-chosen size" (AC12). `textLinking.cursorDocPoint` already tracks the live pointer position
  // while linking is active (see `handleTextLinkPointerMove` in `use-text-link-drag`), so this only
  // needs the gesture's start point and pointer id — no separate RAF-throttled current-position
  // tracking.
  const linkedTextDragStartRef = useRef<{
    pointerId: number
    sourceId: string
    startCanvasX: number
    startCanvasY: number
  } | null>(null)

  // Cancel in-progress text linking on spread switch.
  useEffect(() => {
    setTextLinking(null)
  }, [checkpointLoadKey])

  // Screen-reader announcements for linking-mode transitions (WMPCM-9198). The ports themselves
  // are labeled controls, but entering and leaving linking mode is a state change with no visual
  // anchor an assistive tech would otherwise land on. The live region lives in CenterCanvas
  // rather than `home.tsx` because every piece of linking state is local to it — the `home.tsx`
  // regions are there only because their sources are contexts `home` already holds.
  const [textLinkAnnouncement, setTextLinkAnnouncement] = useState('')
  /** Bumped per message so an identical string repeated back-to-back still re-announces. */
  const [textLinkAriaKey, setTextLinkAriaKey] = useState(0)
  const announceTextLink = useCallback((message: string) => {
    setTextLinkAnnouncement(message)
    setTextLinkAriaKey((key) => key + 1)
  }, [])

  const cancelTextLinking = useCallback(() => {
    setTextLinking(null)
    announceTextLink('Text linking canceled.')
  }, [announceTextLink])

  const handleTextOutPortClick = useCallback(
    (frameId: string) => {
      setTextLinking({ sourceId: frameId, cursorDocPoint: null, hoverTargetId: null })
      announceTextLink(
        'Text linking started. Tab to a frame and press Enter to link, or press Escape to cancel.'
      )
    },
    [announceTextLink]
  )

  // Reads `textLinking` directly rather than through a functional `setTextLinking` updater: the
  // announcement below is a side effect, and an updater must stay pure (StrictMode double-invokes
  // it). Safe to close over — this handler's only consumer is the linking overlay, which
  // re-renders with the state it depends on.
  const handleTextInPortClick = useCallback(
    (targetId: string) => {
      if (!textLinking) return

      if (textLinking.sourceId !== targetId) {
        onTextFramesLinked?.(textLinking.sourceId, targetId)
        announceTextLink('Text frames linked.')
      } else {
        // Aiming a link back at its own source ends the session without linking.
        announceTextLink('Text linking canceled.')
      }

      setTextLinking(null)
    },
    [announceTextLink, onTextFramesLinked, textLinking]
  )

  const handleTextUnlinkClick = useCallback(
    (frameId: string) => {
      onTextFramesUnlinked?.(frameId)
      announceTextLink('Text link removed.')
    },
    [announceTextLink, onTextFramesUnlinked]
  )

  const handleTextPortHoverChange = useCallback((frameId: string | null) => {
    setTextLinking((prev) => (prev ? { ...prev, hoverTargetId: frameId } : prev))
  }, [])

  /**
   * Topmost linkable frame whose box contains a document point, or null. Spans both managers, so a
   * shape converted to a text frame is found the same way a Text-tool frame is (WMPCM-7988) — the
   * eligibility rule lives in the projection, never in a `kindId` check here.
   *
   * `bottomMargin` extends the test below the frame's own bottom edge. The out-port "+" hover needs
   * it (the glyph sits astride that edge, so without it the pointer "leaves" the frame the moment
   * it reaches the glyph's lower half and the "+" flickers away); a link *target* test must not use
   * it, or a frame would claim a strip of canvas below itself.
   */
  const findLinkableFrameAtDocPoint = useCallback(
    (
      docX: number,
      docY: number,
      options?: {
        bottomMargin?: number
        excludeId?: string
        /**
         * Keep locked frames in the hit test, so the caller sees the TRUE topmost frame and can
         * refuse it (WMPCM-10578 #3) instead of silently linking whatever sits underneath.
         */
        includeLocked?: boolean
      }
    ) => {
      const frames = collectLinkableFrames(internalShapeManagerRef.current?.getShapes() ?? [])
      const bottomMargin = options?.bottomMargin ?? 0
      const hits = frames.filter(
        (f) =>
          f.id !== options?.excludeId &&
          (options?.includeLocked === true || !f.locked) &&
          !f.hidden &&
          docX >= f.x &&
          docX <= f.x + f.width &&
          docY >= f.y &&
          docY <= f.y + f.height + bottomMargin
      )

      if (hits.length === 0) return null

      // Real unified z-order, topmost first. This used to be `[...frames].reverse()` with the
      // comment "matching paint order" — which it did not: `collectLinkableFrames` returns
      // `[…textBoxes, …shapes]`, a MANAGER partition, so reversing it merely made every shape
      // outrank every text box regardless of what was actually drawn on top. With two overlapping
      // frames the winner was whichever manager happened to hold the later one, which is how a
      // click aimed at a shape could land on a nearby text box (WMPCM-9070 Bug 3).
      //
      // `unifiedLayerIds` is bottom-to-top (see `elementZIndexMap` in `home.tsx`, index + 1), so a
      // higher index is nearer the viewer. An id missing from the order — a frame created this
      // gesture, before the unified list has caught up — sorts below everything present rather
      // than winning by accident; `sort` is stable, so those keep their previous relative order.
      const zByIdEntries = unifiedLayerIdsRef.current.map((id, i) => [id, i] as const)
      const zById = new Map<string, number>(zByIdEntries)

      return [...hits].sort((a, b) => (zById.get(b.id) ?? -1) - (zById.get(a.id) ?? -1))[0] ?? null
    },
    [internalShapeManagerRef, unifiedLayerIdsRef]
  )

  return {
    textLinking,
    setTextLinking,
    textLinkingRevision,
    setTextLinkingRevision,
    isTextLinkingActive,
    textToolHoverFrameId,
    setTextToolHoverFrameId,
    linkedTextDragStartRef,
    textLinkAnnouncement,
    textLinkAriaKey,
    announceTextLink,
    cancelTextLinking,
    handleTextOutPortClick,
    handleTextInPortClick,
    handleTextUnlinkClick,
    handleTextPortHoverChange,
    findLinkableFrameAtDocPoint,
  }
}
